import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { 
  Search, 
  Upload, 
  Trash2, 
  Copy, 
  Check, 
  Loader2,
  Maximize2,
  Calendar
} from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { SeletorDeImagem } from "@/components/admin/SeletorDeImagem";

export const Route = createFileRoute("/admin/midias")({
  component: AdminMidiasPage,
});

function AdminMidiasPage() {
  const [searchTerm, setSearchTerm] = useState("");
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const { data: midias, isLoading } = useQuery({
    queryKey: ['admin-midias-full', searchTerm],
    queryFn: async () => {
      let query = supabase
        .from('midias')
        .select(`
          *,
          profiles(nome)
        `)
        .order('created_at', { ascending: false });
      
      if (searchTerm) {
        query = query.ilike('nome', `%${searchTerm}%`);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data;
    }
  });

  const deleteMutation = useMutation({
    mutationFn: async (midia: any) => {
      // 1. Check if used in articles
      const { data: articles, error: checkError } = await supabase
        .from('artigos')
        .select('id, titulo')
        .or(`imagem_capa.eq.${midia.arquivo_url},conteudo.ilike.%${midia.arquivo_url}%`);
      
      if (checkError) throw checkError;
      
      if (articles && articles.length > 0) {
        throw new Error(`Esta imagem está sendo usada em ${articles.length} artigo(s): ${articles.map(a => a.titulo).join(', ')}`);
      }

      // 2. Delete from Storage
      const { error: storageError } = await supabase.storage
        .from('midias')
        .remove([midia.caminho]);
      
      if (storageError) throw storageError;

      // 3. Delete from DB
      const { error: dbError } = await supabase
        .from('midias')
        .delete()
        .eq('id', midia.id);
      
      if (dbError) throw dbError;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-midias-full'] });
      toast.success("Mídia excluída com sucesso!");
    },
    onError: (error: any) => {
      toast.error(error.message);
    }
  });

  const copyToClipboard = (url: string, id: string) => {
    navigator.clipboard.writeText(url);
    setCopiedId(id);
    toast.success("URL copiada!");
    setTimeout(() => setCopiedId(null), 2000);
  };

  const formatSize = (bytes: number | null | undefined) => {
    const b = typeof bytes === 'number' ? bytes : parseInt(String(bytes));
    if (isNaN(b) || b <= 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(b) / Math.log(k));
    const safeIndex = Math.min(i, sizes.length - 1);
    return parseFloat((b / Math.pow(k, safeIndex)).toFixed(2)) + ' ' + sizes[safeIndex];
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-[#0C1A12]">Biblioteca de Mídias</h1>
          <p className="text-gray-500">Gerencie imagens e arquivos do blog e plataforma.</p>
        </div>
        <SeletorDeImagem 
          onSelect={() => queryClient.invalidateQueries({ queryKey: ['admin-midias-full'] })}
          trigger={
            <Button className="bg-primary text-[#0C1A12] font-bold">
              <Upload className="w-4 h-4 mr-2" /> Upload de Mídia
            </Button>
          }
        />
      </div>

      <Card>
        <CardHeader>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input 
              placeholder="Buscar mídias por nome..." 
              className="pl-10"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-20">
              <Loader2 className="w-10 h-10 animate-spin text-primary mb-4" />
              <p className="text-gray-500">Carregando biblioteca...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
              {midias?.map((midia) => (
                <div key={midia.id} className="group relative flex flex-col bg-white rounded-2xl border border-gray-100 overflow-hidden hover:shadow-md transition-all">
                  <div className="aspect-video bg-gray-50 relative overflow-hidden">
                    <img 
                      src={midia.arquivo_url} 
                      alt={midia.alt || midia.nome || ""}
                      className="w-full h-full object-cover transition-transform group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                      <Button 
                        size="icon" 
                        variant="secondary" 
                        className="rounded-full w-8 h-8"
                        onClick={() => copyToClipboard(midia.arquivo_url, midia.id)}
                      >
                        {copiedId === midia.id ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      </Button>
                      <Button 
                        size="icon" 
                        variant="destructive" 
                        className="rounded-full w-8 h-8"
                        onClick={() => {
                          if (confirm("Tem certeza que deseja excluir esta mídia? Esta ação não pode ser desfeita.")) {
                            deleteMutation.mutate(midia);
                          }
                        }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="p-3 flex-1 flex flex-col gap-1">
                    <p className="font-bold text-sm text-[#0C1A12] truncate" title={midia.nome || ""}>
                      {midia.nome}
                    </p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      <Badge variant="secondary" className="text-[10px] py-0 px-1 font-normal">
                        {midia.tipo?.split('/')[1]?.toUpperCase() || 'WEBP'}
                      </Badge>
                      <Badge variant="secondary" className="text-[10px] py-0 px-1 font-normal">
                        {midia.largura}x{midia.altura}
                      </Badge>
                      <Badge variant="secondary" className="text-[10px] py-0 px-1 font-normal">
                        {formatSize(midia.tamanho_bytes)}
                      </Badge>
                    </div>
                    <div className="mt-2 pt-2 border-t flex items-center justify-between text-[10px] text-gray-400">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {midia.created_at ? format(new Date(midia.created_at), "dd/MM/yyyy", { locale: ptBR }) : ''}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
              {midias?.length === 0 && (
                <div className="col-span-full py-20 text-center flex flex-col items-center">
                  <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center text-gray-300 mb-4">
                    <Maximize2 className="w-8 h-8" />
                  </div>
                  <p className="text-gray-500 font-medium">Nenhuma mídia encontrada.</p>
                  <p className="text-sm text-gray-400 mt-1">Tente ajustar sua busca ou faça um novo upload.</p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
